/* Time and duration maths for Member Hours.
   =========================================

   THE FIRST CODE TO LEAVE app.html, and chosen for that on purpose: these are
   pure functions. No DOM, no globals, no Firestore. Given the same input they
   return the same output, which makes them the one part of this app that can
   be tested properly without a browser — see time.test.js beside this file.

   The extraction pattern this establishes, and which the rest should follow:
     1. Move something self-contained out first.
     2. Export it, test it, and let the bundle put it back on `window` so the
        inline code that calls it does not change at all.
     3. Verify, ship, repeat.

   Nothing here changed on the way out. If any of it behaves differently from
   the version that lived in app.html, that is a bug, not an improvement.
*/

/* Sunday-anchored week key, YYYY-MM-DD. Member Hours weeks run Sunday to
   Saturday, and every week bucket in the app is keyed by this. */
export function mhWeekStartKey(dateLike) {
  const d = (dateLike instanceof Date)
    ? new Date(dateLike)
    : (typeof dateLike === 'string' ? new Date(dateLike + 'T00:00:00') : new Date());
  // An unparseable date falls back to this week rather than propagating NaN
  // into a key that would then bucket entries somewhere nobody can find them.
  if (isNaN(d.getTime())) return mhWeekStartKey(new Date());
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());   // back to Sunday
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* Minutes between two "HH:MM" strings. Never negative: an end time before the
   start is a typo, and a negative duration would quietly corrupt a week's
   totals rather than showing up as an obviously wrong row. */
export function mhComputeMinutes(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  if ([sh, sm, eh, em].some(n => isNaN(n))) return 0;
  return Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
}

/* Billable units: one per complete half hour, rounded DOWN. A 29-minute visit
   is zero units. This is the billing rule, not a display choice — do not make
   it round to nearest without checking what the county expects. */
export function mhComputeUnits(minutes) {
  return Math.max(0, Math.floor((minutes || 0) / 30));
}

/* "2026-08-17" -> "08/17/26". The format the paper forms use. */
export function mhFormatMMDDYY(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return '';
  return `${m}/${d}/${y.slice(2)}`;
}

/* "14:05" -> "2:05 PM". Midnight and noon are the cases worth remembering:
   hour 0 and hour 12 both display as 12. */
export function mhFormatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return '';
  const p = h >= 12 ? 'PM' : 'AM';
  const h12 = (h % 12) || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${p}`;
}

/* Minutes since midnight, or NaN. Strict about the format on purpose — the
   callers use isNaN() to decide whether a field is usable. */
export function mhTimeToMin(t) {
  if (!t || !/^\d{2}:\d{2}$/.test(t)) return NaN;
  return parseInt(t.slice(0, 2), 10) * 60 + parseInt(t.slice(3, 5), 10);
}

/* The inverse, clamped to a real clock. Past midnight it stops at 23:30 rather
   than wrapping into the next day, because a stepper that rolls a 23:45 finish
   round to 00:15 files the entry on the wrong date. */
export function mhMinToTime(min) {
  if (min < 0) min = 0;
  if (min >= 24 * 60) min = 23 * 60 + 30;
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

/* Step a time by some minutes. Empty string in, empty string out. */
export function mhAddMinutes(t, delta) {
  const m = mhTimeToMin(t);
  if (isNaN(m)) return '';
  return mhMinToTime(m + delta);
}
